import StringUtils from "../utils/StringUtils.js";
import ScoreUtils from "../utils/ScoreUtils.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import AccountSync from "../services/AccountSync.js";
import AccountService from "../services/AccountService.js";
import PlayerIdentity from "../services/PlayerIdentity.js";
import * as FlagPreloader from "../utils/FlagPreloader.js";
import { getFlagPath, renderFlagDuelError } from "../utils/FlagUtils.js";
import { bindAnswerKeys } from "../utils/QuizKeyboard.js";
import { renderErrorsRecap, endGameAnchor } from "../utils/ErrorsRecap.js";
import { enterEndGame } from "../utils/EndGameUI.js";
import { PICTO_FLAME, PICTO_SHIELD, PICTO_BELL } from "../utils/Pictos.js";
import { formatDuration } from "../utils/TimeUtils.js";
import { generateDailyQuestions, versionFor, themeFor, TOTAL_QUESTIONS } from "../utils/DailyQuestions.js";
import { advanceStreak, isStreakAlive, jokerAvailableFor, JOKER_COOLDOWN_DAYS } from "../utils/StreakRules.js";
import { renderAccountNudge } from "../utils/AccountNudge.js";
import * as Passport from "../utils/Passport.js";
import * as ReviewDeck from "../utils/ReviewDeck.js";
import * as PushReminders from "../services/PushReminders.js";
import { renderInstallCard } from "../services/InstallPrompt.js";

/** Modes proposés « en attendant » le prochain défi, une fois les deux défis du jour joués. */
const MEANWHILE_MODES = [
    { slug: "flag-nerd-speed-run", nameKey: "home.flagNerd", modeKey: "home.speedRun" },
    { slug: "map-nerd-classic", nameKey: "home.mapNerd", modeKey: "home.classic" },
    { slug: "capital-nerd-classic", nameKey: "home.capitalNerd", modeKey: "home.classic" },
    { slug: "country-nerd", nameKey: "home.countryNerd", modeKey: "home.classic" },
    { slug: "flag-nerd-tricky", nameKey: "home.flagNerd", modeKey: "home.tricky" },
    { slug: "us-states-classic", nameKey: "home.usStates", modeKey: "home.classic" },
];

/** Tranches de l'historique des scores, de la meilleure à la moins bonne. */
const HISTORY_BUCKETS = [
    { label: "20", min: 20 },
    { label: "18-19", min: 18 },
    { label: "15-17", min: 15 },
    { label: "10-14", min: 10 },
    { label: "0-9", min: 0 },
];

/** En dessous de ce nombre de joueurs du jour, un pourcentage ne veut rien dire. */
const PERCENTILE_MIN_PLAYERS = 5;

export default class DailyChallenge {
    /** Launch date — day 1. Keep in sync with the original getDayNumber epoch. */
    static EPOCH = "2026-04-25";

    constructor(mode = "classic") {
        this.mode = mode;
        this.page = document.querySelector(".daily-challenge");
        if (!this.page) return;

        this.gameId = mode === "hard" ? "dailychallengehard" : "dailychallenge";
        this.storagePrefix = DailyChallenge.STORAGE_PREFIXES[mode === "hard" ? "hard" : "classic"];
        this.progressKey = `${this.storagePrefix}.progress`;
        // Streak is language-agnostic (it counts calendar days, not localized data),
        // so it lives under a raw localStorage key like the per-day result keys.
        this.streakKey = `${this.storagePrefix}.streak`;
        this.totalQuestions = TOTAL_QUESTIONS;
        this.flagContainer = this.page.querySelector(".flag-container");
        this.answerContainer = this.page.querySelector(".answer-container");
        this.endMessage = this.page.querySelector(".end-message");
        this.foundEl = this.page.querySelector(".progress .found");
        this.totalEl = this.page.querySelector(".progress .total");
        this.progressSteps = this.page.querySelector(".progress-steps");

        if (mode === "hard") {
            this.answerInput = this.answerContainer.querySelector(".answer-input");
            this.validateBtn = this.answerContainer.querySelector(".validate-input");
        }

        this.today = this.getToday();
        this.seed = this.dateToSeed(this.today) + (mode === "hard" ? 1000000 : 0);
        this.dayNumber = this.getDayNumber();
        this.score = 0;
        this.currentIndex = 0;
        this.errors = [];
        this.stepResults = [];
        // Un tour ne se joue qu'une fois, et la partie ne se termine qu'une fois :
        // voir `bindClassicEvents()` et `endGame()`.
        this.canAnswer = false;
        this.ended = false;

        this.totalEl.textContent = this.totalQuestions;

        if (this.isAlreadyPlayed()) {
            this.showAlreadyPlayed();
            return;
        }

        // Une partie commencée garde la version du tirage de son début : une mise
        // en ligne en pleine journée ne change pas les questions d'un joueur lancé.
        const saved = this.readProgress();
        this.version = saved ? (saved.v || 1) : versionFor(this.today);
        this.theme = this.version === 1 ? "flags" : themeFor(this.today);

        this.renderThemeBadge(this.theme);
        this.buildProgressSteps();
        this.questions = this.generateQuestions();
        // Fresh value for a new day; restoreProgress() overwrites it with the persisted one
        // when resuming, so the clock is never reset by a reload (see BaseGame._saveBestRun).
        this.gameStartedAt = Date.now();
        this.restoreProgress(saved);
        this.initShare();
        this.initBackButton();
        this.startElapsedTicker();
        // Lire la règle (« Comment jouer ? ») avant la première question ne
        // compte pas dans le temps, qui départage le classement du jour.
        document.addEventListener("geonerd:gameinfo-closed", () => {
            if (this.currentIndex === 0 && !this.ended) this.gameStartedAt = Date.now();
        });
        if (mode === "hard") this.buildHardEvents();
        this.nextQuestion();
    }

    /** Secondes écoulées depuis le vrai début de la partie (reprises comprises). Tronquées, jamais arrondies — voir BaseGame.elapsedSeconds(). */
    elapsedSeconds() {
        return Math.max(0, Math.floor((Date.now() - this.gameStartedAt) / 1000));
    }

    /**
     * Affiche le chrono de la partie en cours, à côté du score — même
     * pattern que `BaseGame.startElapsedTicker()`. Le temps était déjà
     * mesuré et sauvegardé (`saveResult()`), et départageait déjà les ex
     * aequo du classement, mais restait invisible pendant la partie.
     */
    startElapsedTicker() {
        const progress = this.page.querySelector(".progress");
        if (!progress || this.elapsedTimer) return;

        this.elapsedEl = progress.querySelector(".elapsed");
        if (!this.elapsedEl) {
            progress.insertAdjacentHTML("beforeend", `<span class="elapsed"><span class="elapsed-icon" aria-hidden="true"></span><span class="elapsed-value"></span></span>`);
            this.elapsedEl = progress.querySelector(".elapsed");
        }

        const tick = () => {
            const value = this.elapsedEl.querySelector(".elapsed-value");
            if (value) value.textContent = formatDuration(this.elapsedSeconds());
        };
        tick();
        this.elapsedTimer = setInterval(tick, 1000);
    }

    /** Fige le chrono affiché : la partie est finie, il ne doit plus avancer. */
    stopElapsedTicker() {
        if (!this.elapsedTimer) return;
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
    }

    getToday() {
        return DailyChallenge.todayStr();
    }

    dateToSeed(dateStr) {
        return parseInt(dateStr.replace(/-/g, ""), 10);
    }

    getDayNumber() {
        return DailyChallenge.dayNumberFor(this.today);
    }

    /**
     * Calendar day as `YYYY-MM-DD`, in UTC rather than the player's local timezone —
     * the server (leaderboard.js:getTodayStr) computes "today" the same way to look
     * up this same key, so both sides must agree on a single, unambiguous reference
     * instead of each reading their own local clock.
     */
    static todayStr() {
        const d = new Date();
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }

    /**
     * Integer day index since launch. Date-only strings parse as UTC midnight on
     * both sides, so the subtraction yields whole days regardless of timezone —
     * this is the single source of truth for "what day is it" (also used by the
     * home-card streak badge).
     */
    static dayNumberFor(todayStr) {
        const start = new Date(DailyChallenge.EPOCH);
        const now = new Date(todayStr);
        return Math.floor((now - start) / 86400000) + 1;
    }

    /** localStorage prefix per mode — mirrors `this.storagePrefix` (constructor). */
    static STORAGE_PREFIXES = { classic: "geonerd.daily", hard: "geonerd.daily.hard" };

    /**
     * Every localStorage key holding Daily Challenge state, for one mode or both.
     * The dated result keys (`{prefix}.{YYYY-MM-DD}`) can't be listed upfront, so
     * the store is scanned. Used by the Scores-page resets: clearing only
     * `dailychallenge.best` leaves the streak and the dated result behind, and the
     * day then stays flagged "already played" (see `isAlreadyPlayed()`).
     * @param {"classic"|"hard"} [mode] Restrict to a single mode.
     */
    static storedKeys(mode) {
        const prefixes = mode
            ? [DailyChallenge.STORAGE_PREFIXES[mode]]
            : Object.values(DailyChallenge.STORAGE_PREFIXES);
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) continue;
            for (const prefix of prefixes) {
                if (!key.startsWith(`${prefix}.`)) continue;
                // `geonerd.daily.` also prefixes the hard keys, but their suffix
                // ("hard.streak", "hard.2026-08-06") never matches the tests below.
                const suffix = key.slice(prefix.length + 1);
                if (suffix === "progress" || suffix === "streak" || /^\d{4}-\d{2}-\d{2}$/.test(suffix)) {
                    keys.push(key);
                }
            }
        }
        return keys;
    }

    /** Résultats finalisés d'un mode, du plus ancien au plus récent : `[{ date, score }]`. */
    static storedResults(mode) {
        const prefix = DailyChallenge.STORAGE_PREFIXES[mode];
        const results = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(`${prefix}.`)) continue;
            const date = key.slice(prefix.length + 1);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
            try {
                const score = JSON.parse(localStorage.getItem(key))?.score;
                if (typeof score === "number") results.push({ date, score });
            } catch { /* entrée illisible : ignorée */ }
        }
        return results.sort((a, b) => (a.date < b.date ? -1 : 1));
    }

    /**
     * Où se situe un score parmi les joueurs du jour.
     * @returns {{ kind: "few"|"perfect"|"better", pct: number, total: number }}
     */
    static percentile(score, stats, total = TOTAL_QUESTIONS) {
        const players = stats?.total ?? 0;
        const dist = stats?.distribution ?? [];
        if (players < PERCENTILE_MIN_PLAYERS) return { kind: "few", pct: 0, total: players };
        if (score >= total) {
            return { kind: "perfect", pct: Math.round(((dist[total] || 0) / players) * 100), total: players };
        }
        const below = dist.slice(0, score).reduce((sum, n) => sum + (n || 0), 0);
        return { kind: "better", pct: Math.round((below / players) * 100), total: players };
    }

    isAlreadyPlayed() {
        return localStorage.getItem(`${this.storagePrefix}.${this.today}`) !== null;
    }

    buildProgressSteps() {
        if (!this.progressSteps) return;
        this.progressSteps.innerHTML = "";
        for (let i = 0; i < this.totalQuestions; i++) {
            const step = document.createElement("div");
            step.classList.add("step");
            this.progressSteps.appendChild(step);
        }
    }

    /** Thème du jour, sous le titre : on sait d'emblée sur quoi on va être interrogé. */
    renderThemeBadge(theme) {
        const container = this.page.querySelector(".title-container");
        if (!container) return;
        container.querySelector(".daily-theme")?.remove();
        const lang = geoNerdApp.languageManager;
        const label = lang.t("daily.themeToday").replace("{theme}", lang.t(`daily.theme.${theme}`));
        container.insertAdjacentHTML("beforeend", `<p class="daily-theme daily-theme-${theme}">${label}</p>`);
    }

    saveProgress() {
        localStorage.setItem(this.progressKey, JSON.stringify({
            date: this.today,
            v: this.version,
            currentIndex: this.currentIndex,
            score: this.score,
            errors: this.errors,
            stepResults: this.stepResults,
            startedAt: this.gameStartedAt
        }));
    }

    /** Partie du jour en cours, ou `null` (une partie d'un autre jour est effacée). */
    readProgress() {
        const raw = localStorage.getItem(this.progressKey);
        if (!raw) return null;
        try {
            const data = JSON.parse(raw);
            if (data.date === this.today) return data;
        } catch { /* illisible : effacée ci-dessous */ }
        localStorage.removeItem(this.progressKey);
        return null;
    }

    restoreProgress(data = this.readProgress()) {
        if (!data) return;
        this.currentIndex = data.currentIndex;
        this.score = data.score;
        this.errors = data.errors || [];
        this.stepResults = data.stepResults || [];
        if (data.startedAt) this.gameStartedAt = data.startedAt;
        this.updateProgress();
    }

    clearProgress() {
        localStorage.removeItem(this.progressKey);
    }

    /** Read the stored streak, with safe defaults. */
    getStreak() {
        try {
            const raw = localStorage.getItem(this.streakKey);
            if (!raw) return { lastPlayedDay: 0, current: 0, longest: 0 };
            const s = JSON.parse(raw);
            const streak = {
                lastPlayedDay: s.lastPlayedDay || 0,
                current: s.current || 0,
                longest: s.longest || 0
            };
            if (s.jokerUsedDay) streak.jokerUsedDay = s.jokerUsedDay;
            return streak;
        } catch (e) {
            return { lastPlayedDay: 0, current: 0, longest: 0 };
        }
    }

    /**
     * Advance the consecutive-day streak (see StreakRules: a missed day can be
     * covered by the joker). Compared by integer day index, never by date math.
     * Idempotent: replaying or re-rendering the same day never double-counts.
     */
    updateStreak() {
        const { streak, jokerUsed } = advanceStreak(this.getStreak(), this.dayNumber);
        this.jokerUsedToday = jokerUsed;
        localStorage.setItem(this.streakKey, JSON.stringify(streak));
        return streak;
    }

    saveResult(score) {
        this.clearProgress();
        const duration = Math.round((Date.now() - this.gameStartedAt) / 1000);
        localStorage.setItem(`${this.storagePrefix}.${this.today}`, JSON.stringify({
            score,
            total: this.totalQuestions,
            results: this.results,
            errors: this.errors,
            errorCount: this.errors.length,
            duration,
            theme: this.theme,
        }));
        // Finalizing the day is the single place that advances the streak.
        this.streak = this.updateStreak();
        ScoreUtils.saveStats(this.gameId, score);

        const lang = geoNerdApp.languageManager;
        const bestKey = lang.getStorageKey(`${this.gameId}.best`);
        const currentBest = parseInt(localStorage.getItem(bestKey) || "0");
        if (score > currentBest) {
            localStorage.setItem(bestKey, score.toString());
        }
        // Push immédiat (pas de debounce) : le lien "classement" affiché juste après
        // est cliquable avant qu'un push différé n'ait lieu.
        AccountSync.push();
        return duration;
    }

    generateQuestions() {
        return generateDailyQuestions({
            countries: geoNerdApp.countries,
            seed: this.seed,
            mode: this.mode,
            date: this.today,
            version: this.version,
        });
    }

    nextQuestion() {
        if (this.currentIndex >= this.questions.length) {
            this.endGame();
            return;
        }

        const index = this.currentIndex;
        const q = this.questions[index];
        this.rightAnswer = q.country;
        this.currentType = q.type;
        this.canAnswer = false;

        const render = () => this.renderTurn(q);
        // Un essai par jour : la question n'apparaît qu'avec son drapeau.
        if (!FlagPreloader.isActive() || FlagPreloader.areLoaded([q.country.code])) {
            render();
            return;
        }
        this.flagContainer.style.opacity = "1";
        this.flagContainer.innerHTML = '<div class="flag-loader"></div>';
        if (this.mode !== "hard") this.answerContainer.innerHTML = "";
        FlagPreloader.load([q.country.code]).then(() => {
            if (this.currentIndex === index && !this.ended) render();
        });
    }

    renderTurn(q) {
        this.flagContainer.style.opacity = "1";
        this.answerContainer.style.opacity = "1";
        this.flagContainer.innerHTML = this.renderQuestion(q);
        this.canAnswer = true;

        if (this.mode === "hard") {
            const lang = geoNerdApp.languageManager;
            this.answerInput.placeholder = lang.t(q.type === "capital" ? "daily.typeCapital" : "game.typeCountryName");
            this.answerInput.value = "";
            this.answerInput.focus();
        } else {
            this.answerContainer.innerHTML = "";
            q.proposals.forEach(p => {
                const label = q.type === "capital" ? p.capital : p.name;
                this.answerContainer.insertAdjacentHTML("beforeend",
                    `<button type="button" class="country" data-country-code="${p.code}">${label}</button>`
                );
            });
            this.answerContainer.style.pointerEvents = "initial";
            // Raccourcis 1–4, posés une fois : ils relisent les boutons à la frappe.
            if (!this.unbindAnswerKeys) this.unbindAnswerKeys = bindAnswerKeys(this.answerContainer);
            this.bindClassicEvents();
        }
    }

    /** Drapeau seul, ou pays (drapeau + nom) dont il faut trouver la capitale. */
    renderQuestion(q) {
        const flag = `<div class="flag" style="background-image: url(${getFlagPath(q.country.code)})"></div>`;
        if (q.type !== "capital") return flag;
        const lang = geoNerdApp.languageManager;
        return `
            <div class="daily-capital">
                ${flag}
                <p class="daily-capital-country">${q.country.name}</p>
                <p class="daily-capital-prompt">${lang.t("daily.capitalQuestion")}</p>
            </div>`;
    }

    buildHardEvents() {
        this.answerInput.addEventListener("keydown", e => {
            // preventDefault stops the mobile browser's default action on the
            // virtual keyboard's Enter/Go key (which otherwise blurs the field
            // and closes the keyboard).
            if (e.key === "Enter" && this.canAnswer) {
                e.preventDefault();
                this.validateHardAnswer(this.answerInput.value);
                this.keepKeyboardOpen();
            }
        });
        const submit = () => {
            if (this.canAnswer) this.validateHardAnswer(this.answerInput.value);
            this.keepKeyboardOpen();
        };
        // Act on touchstart (earliest touch event) + preventDefault: iOS steals
        // the first tap on a control while the keyboard is open to dismiss it
        // (firing touchcancel, not click/touchend), so those never run.
        // touchstart fires before that; preventDefault keeps focus on the input
        // (keyboard stays) and suppresses the emulated click (no double-submit).
        this.validateBtn.addEventListener("touchstart", e => { e.preventDefault(); submit(); }, { passive: false });
        this.validateBtn.addEventListener("click", submit);
    }

    // Re-focus the input synchronously, inside the user gesture, so the mobile
    // keyboard stays/re-opens between questions (Android closes it on the
    // keyboard's Enter/Go key even with preventDefault). No-op after the last
    // question (input is about to be hidden).
    keepKeyboardOpen() {
        if (this.mode === "hard" && this.currentIndex < this.totalQuestions) {
            this.answerInput.focus({ preventScroll: true });
        }
    }

    /** Réponse tapée juste ? Nom du pays, ou capitale les jours de capitales. */
    matchesHardAnswer(answer) {
        if (this.currentType === "capital") {
            return StringUtils.matchesAnswer(answer, this.rightAnswer, geoNerdApp.countries, c => [c.capital]);
        }
        return StringUtils.matchesAnswer(answer, this.rightAnswer, geoNerdApp.countries);
    }

    validateHardAnswer(answer) {
        // Valider à vide (Entrée réflexe, second appui après avoir effacé) ne coûte
        // rien : sans réponse il n'y a pas de faute à sanctionner, et la question
        // du jour ne doit pas être consommée pour autant.
        if (!answer || !answer.trim()) return;
        this.canAnswer = false;
        const isCorrect = this.matchesHardAnswer(answer);

        const feedbackColor = isCorrect ? "#33ce29" : "#F05050";
        gsap.to(this.answerInput, {
            backgroundColor: feedbackColor,
            color: "white",
            duration: 0.3,
            onComplete: () => {
                gsap.set(this.answerInput, { backgroundColor: "white", color: "black", delay: 0.8 });
            }
        });

        if (isCorrect) {
            SoundManager.correct();
            FeedbackEffects.confetti(this.answerInput);
            this.score++;
            Passport.stamp(this.rightAnswer.code, this.currentType === "capital" ? "capital" : "flag");
            this.stepResults.push("correct");
        } else {
            SoundManager.wrong();
            FeedbackEffects.shake(this.page.querySelector(".game-container"));
            this.errors.push({
                type: this.currentType,
                countryName: this.rightAnswer.name,
                correctCode: this.rightAnswer.code,
                ...(this.currentType === "capital" ? { correctCapital: this.rightAnswer.capital } : {}),
                guessedText: answer
            });
            ReviewDeck.recordMiss(this.currentType === "capital" ? "capital" : "flag", this.rightAnswer.code);
            this.stepResults.push("wrong");
        }

        this.currentIndex++;
        this.updateProgress();
        this.saveProgress();

        setTimeout(() => {
            gsap.to([this.flagContainer, this.answerContainer], {
                opacity: 0,
                duration: 0.4,
                onComplete: () => {
                    this.flagContainer.style.opacity = "1";
                    this.answerContainer.style.opacity = "1";
                    this.nextQuestion();
                }
            });
        }, 700);
    }

    bindClassicEvents() {
        this.answerContainer.querySelectorAll(".country").forEach(btn => {
            btn.addEventListener("click", () => {
                // `pointer-events: none` n'arrête que la souris : les boutons répondus
                // restent dans le DOM (et gardent leurs écouteurs) pendant les ~1,1 s de
                // transition, et un bouton focalisé déclenche encore `click` sur
                // Entrée/Espace. Sans ce drapeau, deux appuis rapprochés consommaient
                // deux questions du défi, dont une répondue à l'aveugle. Même garde que
                // BaseQuizGame et que le mode hard ci-dessous.
                if (!this.canAnswer) return;
                this.canAnswer = false;
                this.answerContainer.style.pointerEvents = "none";
                const isCorrect = btn.dataset.countryCode === this.rightAnswer.code;

                if (isCorrect) {
                    SoundManager.correct();
                    FeedbackEffects.confetti(btn);
                    btn.classList.add("valid");
                    this.score++;
                    Passport.stamp(this.rightAnswer.code, this.currentType === "capital" ? "capital" : "flag");
                    this.stepResults.push("correct");
                } else {
                    SoundManager.wrong();
                    FeedbackEffects.shake(this.page.querySelector(".game-container"));
                    btn.classList.add("invalid");
                    this.answerContainer.querySelector(`[data-country-code="${this.rightAnswer.code}"]`)?.classList.add("valid");
                    const error = {
                        type: this.currentType,
                        countryName: this.rightAnswer.name,
                        correctCode: this.rightAnswer.code,
                        guessedCode: btn.dataset.countryCode
                    };
                    if (this.currentType === "capital") {
                        error.correctCapital = this.rightAnswer.capital;
                        error.guessedCapital = btn.textContent;
                    }
                    this.errors.push(error);
                    ReviewDeck.recordMiss(this.currentType === "capital" ? "capital" : "flag", this.rightAnswer.code);
                    this.stepResults.push("wrong");
                }

                this.currentIndex++;
                this.updateProgress();
                this.saveProgress();

                setTimeout(() => {
                    gsap.to([this.flagContainer, this.answerContainer], {
                        opacity: 0,
                        duration: 0.4,
                        onComplete: () => this.nextQuestion()
                    });
                }, 700);
            });
        });
    }

    updateProgress() {
        this.foundEl.textContent = this.score;
        if (!this.progressSteps) return;
        const steps = this.progressSteps.querySelectorAll(".step");
        steps.forEach((step, i) => {
            step.classList.remove("correct", "wrong");
            if (i < this.stepResults.length) {
                step.classList.add(this.stepResults[i]);
            }
        });
    }

    /**
     * Fin de partie. Idempotente : `nextQuestion()` peut y mener plusieurs fois si
     * deux transitions se chevauchent, et `saveResult()`/`renderStreak()` ne doivent
     * finaliser la journée qu'une fois.
     */
    endGame() {
        if (this.ended) return;
        this.ended = true;
        this.canAnswer = false;
        this.stopElapsedTicker();

        if (this.score > this.totalQuestions / 2) {
            SoundManager.victory();
        } else {
            SoundManager.gameOver();
        }

        this.results = this.questions.map(q => {
            return !this.errors.find(e => e.correctCode === q.country.code);
        });

        const duration = this.saveResult(this.score);

        enterEndGame(this.page);
        if (this.flagContainer) this.flagContainer.style.display = "none";
        if (this.answerContainer) this.answerContainer.style.display = "none";

        this.renderEndTitle();
        const lang = geoNerdApp.languageManager;
        const modeLabel = this.mode === "hard" ? lang.t("home.hard") : lang.t("home.classic");
        ScoreUtils.renderEndGameResults(this.endMessage, this.gameId, `${lang.t("game.dailyChallenge")} ${modeLabel}`, this.score, this.totalQuestions, "daily", { duration });
        this.renderEndExtras(this.score, this.streak, true);

        this.endMessage.classList.add("show");
        this.page.querySelector(".progress-container").style.display = "none";
    }

    showAlreadyPlayed() {
        const data = JSON.parse(localStorage.getItem(`${this.storagePrefix}.${this.today}`));
        this.theme = data.theme || "flags";
        this.renderThemeBadge(this.theme);
        enterEndGame(this.page);
        if (this.flagContainer) this.flagContainer.style.display = "none";
        if (this.answerContainer) this.answerContainer.style.display = "none";

        const title = this.renderEndTitle();
        const lang = geoNerdApp.languageManager;
        const modeLabel = this.mode === "hard" ? lang.t("home.hard") : lang.t("home.classic");
        ScoreUtils.renderEndGameResults(this.endMessage, this.gameId, `${lang.t("game.dailyChallenge")} ${modeLabel}`, data.score, data.total, "daily", { duration: data.duration });

        const grid = this.buildResultGrid(data.results);
        const existing = this.endMessage.querySelector(".daily-grid");
        if (existing) existing.remove();
        title.insertAdjacentHTML("afterend", `<div class="daily-grid">${grid}</div>`);

        this.errors = data.errors || [];
        this.renderEndExtras(data.score, this.getStreak(), false);

        this.endMessage.classList.add("show");
        this.page.querySelector(".progress-container").style.display = "none";
        this.initShare(data.score, data.total, data.results);
        this.initBackButton();
    }

    renderEndTitle() {
        const lang = geoNerdApp.languageManager;
        const modeLabel = this.mode === "hard" ? lang.t("home.hard") : lang.t("home.classic");
        const title = this.endMessage.querySelector(".end-title");
        title.innerHTML = `${lang.t("game.dailyChallenge")} #${this.dayNumber} - ${modeLabel}`;
        return title;
    }

    /**
     * Tout ce qui suit la carte de score, dans l'ordre de lecture : place parmi
     * les joueurs du jour, série, historique, prochain défi, proposition de
     * compte, fautes. Chaque bloc s'insère juste avant la rangée d'actions.
     */
    renderEndExtras(score, streak, justFinished) {
        // Un bloc qui échoue (navigateur ancien, stockage plein) ne doit jamais
        // emporter les suivants : la série et les fautes restent l'essentiel.
        const blocks = [
            () => this.renderPercentile(score),
            () => this.renderStreak(streak, isStreakAlive(streak, this.dayNumber), justFinished),
            () => this.renderHistory(score),
            () => this.renderNext(),
            () => this.renderReminderOffer(),
            () => {
                if (isStreakAlive(streak, this.dayNumber) && streak.current >= 2) {
                    renderAccountNudge(this.endMessage, "streak", { streak: streak.current }, endGameAnchor(this.endMessage));
                }
            },
            () => this.renderErrors(),
        ];
        for (const block of blocks) {
            try {
                block();
            } catch (e) {
                console.error(e);
            }
        }
    }

    insertBeforeActions(html) {
        const anchor = endGameAnchor(this.endMessage);
        if (anchor) anchor.insertAdjacentHTML("beforebegin", html);
        else this.endMessage.insertAdjacentHTML("beforeend", html);
    }

    buildResultGrid(results) {
        if (!results) return "";
        return results.map(r => r ? "🟩" : "🟥").join("");
    }

    /**
     * « Mieux que X % des joueurs aujourd'hui ». Le score part anonymement (un
     * identifiant aléatoire par appareil, voir server/src/routes/daily.js), puis
     * la répartition du jour revient. Hors ligne, le bloc reste simplement vide.
     * Renvoyer le score à la relecture du jour ne double rien : le serveur garde
     * le premier.
     */
    renderPercentile(score) {
        this.endMessage.querySelector(".daily-percentile")?.remove();
        this.insertBeforeActions(`<p class="daily-percentile" hidden></p>`);
        const box = this.endMessage.querySelector(".daily-percentile");
        const lang = geoNerdApp.languageManager;
        const device = PlayerIdentity.getOrCreateId();

        return AccountService.postDailyScore(device, this.today, this.mode, score)
            .then(() => AccountService.getDailyStats(this.today, this.mode))
            .then(({ ok, data }) => {
                if (!ok || !data) return;
                const { kind, pct, total } = DailyChallenge.percentile(score, data, this.totalQuestions);
                box.textContent = lang.t(`daily.percentile.${kind}`)
                    .replace("{pct}", pct)
                    .replace("{total}", total);
                box.hidden = false;
            })
            .catch(() => {});
    }

    /**
     * Render the streak block (flame + current/best), the joker status and the
     * "come back tomorrow" teaser. No-op when the streak is broken/empty.
     */
    renderStreak(streak, alive, justFinished) {
        const existing = this.endMessage.querySelector(".daily-streak");
        if (existing) existing.remove();
        if (!alive || streak.current < 1) return;
        const lang = geoNerdApp.languageManager;

        let joker = "";
        if (justFinished && this.jokerUsedToday) {
            joker = lang.t("daily.jokerUsed");
        } else if (streak.current >= 2) {
            const tomorrow = this.dayNumber + 1;
            joker = jokerAvailableFor(streak, tomorrow)
                ? lang.t("daily.jokerReady")
                : lang.t("daily.jokerRecharge").replace("{n}", streak.jokerUsedDay + JOKER_COOLDOWN_DAYS - tomorrow);
        }

        this.insertBeforeActions(`
            <div class="daily-streak">
                <div class="streak-main">
                    <span class="streak-flame">${PICTO_FLAME}</span><span class="streak-count">${streak.current}</span>
                </div>
                <div class="streak-sub">${lang.t("game.streak")} · ${lang.t("game.streakBest")} ${streak.longest}</div>
                ${joker ? `<p class="streak-joker">${PICTO_SHIELD} ${joker}</p>` : ""}
                <p class="streak-tease">${lang.t("game.comeBackTomorrow")}</p>
            </div>`);
    }

    /** Historique de mes défis de ce mode, par tranche de score (à la Wordle). */
    renderHistory(todayScore) {
        this.endMessage.querySelector(".daily-history")?.remove();
        const results = DailyChallenge.storedResults(this.mode === "hard" ? "hard" : "classic");
        if (!results.length) return;
        const lang = geoNerdApp.languageManager;

        const counts = HISTORY_BUCKETS.map(() => 0);
        results.forEach(({ score }) => {
            counts[HISTORY_BUCKETS.findIndex(b => score >= b.min)]++;
        });
        const todayBucket = HISTORY_BUCKETS.findIndex(b => todayScore >= b.min);
        const max = Math.max(...counts, 1);
        const average = Math.round((results.reduce((s, r) => s + r.score, 0) / results.length) * 10) / 10;

        const rows = HISTORY_BUCKETS.map((bucket, i) => `
            <div class="history-row${i === todayBucket ? " is-today" : ""}">
                <span class="history-label">${bucket.label}</span>
                <span class="history-bar" style="width: ${Math.max(8, Math.round((counts[i] / max) * 100))}%">${counts[i]}</span>
            </div>`).join("");

        const summary = lang.t(results.length === 1 ? "daily.historySummaryOne" : "daily.historySummary")
            .replace("{n}", results.length)
            .replace("{avg}", String(average).replace(".", lang.currentLang === "fr" ? "," : "."));
        this.insertBeforeActions(`
            <div class="daily-history">
                <h3>${lang.t("daily.historyTitle")}</h3>
                <p class="history-summary">${summary}</p>
                ${rows}
            </div>`);
    }

    /** Compte à rebours jusqu'au prochain défi (minuit UTC) et suggestion pour patienter. */
    renderNext() {
        this.endMessage.querySelector(".daily-next")?.remove();
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        const lang = geoNerdApp.languageManager;
        const suggestion = this.meanwhileSuggestion();

        this.insertBeforeActions(`
            <div class="daily-next">
                <p class="daily-countdown">${lang.t("daily.nextIn")} <strong class="countdown-value"></strong></p>
                <p class="daily-meanwhile">${lang.t("daily.meanwhile")} <a class="daily-meanwhile-link" href="${suggestion.href}">${suggestion.label}</a></p>
            </div>`);

        const value = this.endMessage.querySelector(".countdown-value");
        const tick = () => {
            const ms = DailyChallenge.msUntilNextChallenge();
            if (ms <= 0) {
                clearInterval(this.countdownTimer);
                value.textContent = lang.t("daily.newChallengeReady");
                return;
            }
            value.textContent = DailyChallenge.formatCountdown(ms);
        };
        tick();
        this.countdownTimer = setInterval(tick, 1000);
    }

    /**
     * « Me rappeler le défi demain » : notification push, sur demande, si le
     * navigateur et le serveur la permettent et que l'appareil n'est pas déjà
     * abonné. Sinon (iPhone hors application installée), l'invite d'installation.
     */
    renderReminderOffer() {
        this.endMessage.querySelector(".daily-reminder")?.remove();
        this.insertBeforeActions(`<div class="daily-reminder" hidden></div>`);
        const box = this.endMessage.querySelector(".daily-reminder");
        const lang = geoNerdApp.languageManager;

        return (async () => {
            if (!(await PushReminders.isAvailable())) {
                renderInstallCard(box);
                return;
            }
            if (await PushReminders.currentSubscription()) return;
            box.innerHTML = `
                <button type="button" class="btn btn-secondary daily-reminder-cta">${PICTO_BELL} ${lang.t("daily.remindMe")}</button>
                <p class="daily-reminder-note">${lang.t("daily.remindMeNote")}</p>`;
            box.hidden = false;
            box.querySelector(".daily-reminder-cta").addEventListener("click", async (e) => {
                e.currentTarget.disabled = true;
                const result = await PushReminders.subscribe();
                const note = box.querySelector(".daily-reminder-note");
                if (result === "subscribed") {
                    const hour = PushReminders.storedHour();
                    note.textContent = lang.t("daily.remindMeDone").replace("{hour}", lang.currentLang === "en" ? `${hour}:00` : `${hour} h`);
                    e.currentTarget.remove();
                } else {
                    note.textContent = lang.t(result === "denied" ? "reminders.pushBlocked" : "reminders.error");
                    e.currentTarget.disabled = false;
                }
            });
        })().catch(() => {});
    }

    /** L'autre défi du jour s'il n'est pas joué, sinon un mode qui change chaque jour. */
    meanwhileSuggestion() {
        const lang = geoNerdApp.languageManager;
        const prefix = lang.currentLang === "en" ? "/en" : "";
        const otherMode = this.mode === "hard" ? "classic" : "hard";
        const otherPlayed = localStorage.getItem(`${DailyChallenge.STORAGE_PREFIXES[otherMode]}.${this.today}`) !== null;
        if (!otherPlayed) {
            return {
                href: `${prefix}/${otherMode === "hard" ? "daily-challenge-hard" : "daily-challenge"}`,
                label: lang.t(otherMode === "hard" ? "daily.tryHard" : "daily.tryClassic"),
            };
        }
        const pick = MEANWHILE_MODES[this.dayNumber % MEANWHILE_MODES.length];
        return { href: `${prefix}/${pick.slug}`, label: `${lang.t(pick.nameKey)} · ${lang.t(pick.modeKey)}` };
    }

    static msUntilNextChallenge(now = Date.now()) {
        const d = new Date(now);
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) - now;
    }

    static formatCountdown(ms) {
        const total = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return [h, m, s].map(n => String(n).padStart(2, "0")).join(":");
    }

    renderErrors() {
        const lang = geoNerdApp.languageManager;
        renderErrorsRecap(this.endMessage, this.errors, err => {
            if (err.type === "capital") {
                const guessed = err.guessedCapital ?? err.guessedText ?? "";
                return `
                    <div class="error-item">
                        <div class="error-country">${err.countryName}</div>
                        <div class="error-flag correct">
                            <img src="${getFlagPath(err.correctCode)}" alt="${err.countryName}">
                        </div>
                        <div class="error-details">
                            <div class="error-answer-text correct">${err.correctCapital}</div>
                            <div class="error-answer-text wrong">${guessed}</div>
                        </div>
                    </div>
                `;
            }
            if (this.mode === "hard") {
                return `
                    <div class="error-item">
                        <div class="error-flag correct">
                            <img src="${getFlagPath(err.correctCode)}" alt="${err.countryName}">
                        </div>
                        <div class="error-details">
                            <div class="error-country">${err.countryName}</div>
                            <div class="error-answer-text wrong">${err.guessedText}</div>
                        </div>
                    </div>
                `;
            }
            return renderFlagDuelError(err);
        }, lang.t("game.yourMistakes"));
    }

    initShare(overrideScore, overrideTotal, overrideResults) {
        this.page.querySelectorAll(".btn-share").forEach(btn => {
            btn.addEventListener("click", () => {
                const score = overrideScore ?? this.score;
                const total = overrideTotal ?? this.totalQuestions;
                const results = overrideResults ?? this.results;
                const grid = this.buildResultGrid(results);
                const lang = geoNerdApp.languageManager;
                const langPrefix = lang.currentLang === "en" ? "/en" : "";
                const modeEmoji = this.mode === "hard" ? "🟥" : "🟦";
                const modeLabel = this.mode === "hard" ? "Hard" : "Classic";
                const slug = this.mode === "hard" ? "daily-challenge-hard" : "daily-challenge";
                const themeLabel = DailyChallenge.SHARE_THEMES[this.theme] || "";

                // Brag the streak only when it's a real run (≥ 2 days) and still alive.
                const streak = this.getStreak();
                const streakCount = isStreakAlive(streak, this.dayNumber) ? streak.current : 0;
                const streakLine = streakCount >= 2 ? `\n🔥 ${streakCount}` : "";

                const text = `#GeoNerd | Daily Challenge #${this.dayNumber} ${modeEmoji} ${modeLabel}${themeLabel} 🌍\n\n${grid}\n${score}/${total}${streakLine}\n\nhttps://geo-nerd.com${langPrefix}/${slug}`;
                navigator.clipboard.writeText(text).then(() => {
                    const textEl = btn.querySelector(".share-text");
                    const original = textEl.textContent;
                    textEl.textContent = lang.t("scores.copied");
                    btn.classList.add("copied");
                    setTimeout(() => {
                        textEl.textContent = original;
                        btn.classList.remove("copied");
                    }, 2000);
                });
            });
        });
    }

    /** Thème dans le texte partagé (en anglais, comme le reste de la ligne). Rien pour les drapeaux. */
    static SHARE_THEMES = { flags: "", capitals: " · Capitals", mix: " · Mix" };

    initBackButton() {
        const home = window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/";
        this.page.querySelectorAll(".reload").forEach(btn => {
            btn.addEventListener("click", () => window.location.href = home);
        });
    }
}
